import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { useTranslation } from 'next-i18next'
import React, { useEffect } from 'react'
import LocationSearchCard, {
  CARD_CAROUSEL_ARROW_TOP
} from 'src/components/LocationSearchCard'
import { CARD_LOCATION_FIELDS } from 'src/constants/fragments'
import { PILLAR_PAGE_MAP, PillarPage } from 'src/constants/searchPages'
import useLogger from 'src/context/LoggerProvider/useLogger'
import usePillarNames from 'src/hooks/usePillarNames'
import {
  IConfiguration,
  ICoordinates,
  ILocation,
  IQuery,
  IQueryRecommendedLocationsArgs
} from 'src/types/graphql'
import { cleanAccentLocation } from 'src/utils/address'
import {
  isSimilarLocation,
  similarSizeFilter
} from 'src/utils/similarLocations'
import Carousel from '../Carousel/Carousel'
import LoadingRecommendedCard from '../Loaders/LoadingRecommendedCard'

const RECOMMENDED_LOCATIONS = gql`
  ${CARD_LOCATION_FIELDS}
  query RecommendedLocations(
    $pagination: PaginationInput!
    $searchInput: SearchLocationsInput!
  ) {
    recommendedLocations(pagination: $pagination, searchInput: $searchInput) {
      total
      hits {
        ...CardLocationFields
      }
      fallback
    }
  }
`

interface SpotsSectionProps {
  location: ILocation
  activeConfig: IConfiguration
}

const SpotsSection = ({
  location: { id, coordinates, addressDetails },
  activeConfig
}: SpotsSectionProps) => {
  const lat = (coordinates as ICoordinates)?.lat
  const lng = (coordinates as ICoordinates)?.lng

  const logger = useLogger()
  const { t } = useTranslation('details')

  const pillarQuery =
    PILLAR_PAGE_MAP[activeConfig.pillarPage as PillarPage]?.query
  const sizeFilter = similarSizeFilter(activeConfig)

  const skip = !lat || !lng

  const {
    data,
    error: recError,
    loading
  } = useQuery<
    Pick<IQuery, 'recommendedLocations'>,
    IQueryRecommendedLocationsArgs
  >(RECOMMENDED_LOCATIONS, {
    skip,
    variables: {
      pagination: { pageSize: 30, from: 0 },
      searchInput: {
        center: { lat: lat!, lng: lng! },
        zoom: 10,
        ...pillarQuery,
        ...(sizeFilter ? { sizes: [sizeFilter] } : {})
      }
    }
  })

  useEffect(() => {
    if (recError) {
      logger.error('Error retrieving locations.', recError)
    }
  }, [recError, logger])

  const locations = (data?.recommendedLocations?.hits?.filter((loc) =>
    isSimilarLocation(loc, id)
  ) ?? []) as ILocation[]

  // Locale-aware: `PILLAR_PAGE_MAP.searchName` is Spanish-only, so reading it
  // directly produced "Mini bodegas en renta near Buenos Aires" on /en.
  const { searchName } = usePillarNames(activeConfig.pillarPage as PillarPage)

  // The recommended-locations query is centered on this listing's coordinates,
  // so the cards are geographically near *the listing*, not the user. Pick the
  // most specific clean place name available (colonia → city) for the headline
  // accent. If neither passes the cleanup filter, fall back to "nearby".
  // Colonia → "near {colonia}" (the specific neighborhood feels right next to
  // a place name). City → "in {city}" (using "near {city}" reads odd when the
  // listing IS in that city).
  const colonia = cleanAccentLocation(addressDetails?.colonia)
  const city = cleanAccentLocation(addressDetails?.city)
  const accentText = colonia
    ? t('SIMILAR.ACCENT_NEAR_PLACE', { place: colonia })
    : city
      ? t('SIMILAR.ACCENT_IN_PLACE', { place: city })
      : t('SIMILAR.ACCENT_FALLBACK')

  if (!loading && locations.length === 0) return null

  return (
    <section>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-12 md:gap-12">
        <div className="md:col-span-5">
          <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
            {t('SIMILAR.EYEBROW_NEAR')}
          </p>
          <h2 className="mt-3 max-w-md font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[40px]">
            {t('SIMILAR.TITLE_PREFIX', { searchName })}
            <span className="font-display text-blue-500 italic">
              {accentText}
            </span>
          </h2>
        </div>
        <p className="max-w-md text-[14px] leading-relaxed text-ink-200 md:col-span-7 md:self-end md:text-[15px]">
          {t('SIMILAR.SUBTITLE')}
        </p>
      </div>

      <div className="mt-10 md:mt-14">
        {!locations || loading ? (
          <LoadingRecommendedCard />
        ) : (
          <div className="relative overflow-hidden md:[mask-image:_linear-gradient(to_right,_transparent_0,_white_40px,white_calc(100%-40px),_transparent_100%)]">
            <Carousel
              carouselOptions={{
                loop: true,
                containScroll: false,
                dragFree: true
              }}
              carouselOptionsDesktop={{ align: 'start' }}
              items={locations}
              showButtons
              marginButtons={4}
              arrowTopClassName={CARD_CAROUSEL_ARROW_TOP}
            >
              {({ item }) => (
                <div key={item.id} className="pb-4">
                  <LocationSearchCard
                    location={item}
                    surface="listing_similar"
                    styleDefault={false}
                  />
                </div>
              )}
            </Carousel>
          </div>
        )}
      </div>
    </section>
  )
}

export default React.memo(SpotsSection)
